import { Box, Popper, PopperProps, useTheme } from "@mui/material";
import { CSSProperties, ReactNode, RefObject, useEffect } from "react";
import { usePopper } from "../../hooks";

interface IHPopper extends Partial<Omit<PopperProps, "container">> {
    Content:
        | ReactNode
        | ((props: {
              anchorRef: RefObject<any>;
              isPopperOpen: boolean;
              togglePopper: (open: boolean) => void;
              handleInputFocused: (isFocused: boolean) => void;
          }) => ReactNode);
    children: ReactNode;
    portal?: PopperProps["container"];
    anchorStyle?: CSSProperties;
    popperStyle?: CSSProperties;
    containerStyle?: CSSProperties;
    onClickOverride?: Function;
    showAction?: "click" | "hover";
    onClick?: () => void;
    onOpenChange?: (open: boolean) => void;
}

const HPopper = ({
    Content,
    children,
    placement = "bottom-start",
    portal,
    anchorStyle,
    popperStyle,
    containerStyle,
    onClickOverride,
    showAction = "click",
    onClick,
    onOpenChange,
    ...props
}: IHPopper) => {
    const theme = useTheme();
    const { anchorRef, popperRef, isPopperOpen, togglePopper, handleInputFocused, anchorEl } = usePopper();

    useEffect(() => {
        onOpenChange?.(isPopperOpen);
    }, [isPopperOpen]);

    const handleWrapperClick = () => {
        onClick?.();
        if (onClickOverride) {
            onClickOverride();
        } else if (showAction === "click") {
            togglePopper(!isPopperOpen);
        }
    };

    const handleMouseEnter = () => {
        if (showAction === "hover") {
            togglePopper(true);
        }
    };

    const handleMouseLeave = () => {
        if (showAction === "hover") {
            togglePopper(false);
        }
    };

    const handleTouchStart = () => {
        if (showAction === "hover") {
            togglePopper(!isPopperOpen);
        }
    };

    return (
        <div
            style={{ width: "fit-content", ...anchorStyle }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
        >
            <div ref={anchorRef} onClick={handleWrapperClick} data-testid="toggle-button">
                {children}
            </div>
            <Popper
                open={isPopperOpen}
                ref={popperRef}
                anchorEl={anchorEl}
                placement={placement}
                container={portal}
                {...props}
                style={{ zIndex: 1300, ...popperStyle }}
            >
                <Box
                    sx={{
                        margin: "0.5rem 0",
                        background: theme.palette.primaryWhite.main,
                        border: theme.styles.altBorder1,
                        ...theme.styles.boxShadows?.[0],
                        padding: "0",
                        borderRadius: "2px",
                        ...containerStyle,
                    }}
                >
                    {typeof Content === "function"
                        ? Content({ anchorRef, isPopperOpen, togglePopper, handleInputFocused })
                        : Content}
                </Box>
            </Popper>
        </div>
    );
};

export default HPopper;
